import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CaseStudyModal = ({ isOpen, onClose, study }) => {
  // Focus trap & Escape key listener for Accessibility
  useEffect(() => {
    if (!isOpen) return;

    const modalElement = document.getElementById("casestudy-modal-box");
    if (modalElement) {
        const closeButton = modalElement.querySelector("button");
        if (closeButton) {
            const timer = setTimeout(() => closeButton.focus(), 100);
            return () => clearTimeout(timer);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            onClose();
            return;
        }

        if (e.key === "Tab" && modalElement) {
            const focusableElements = modalElement.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements.length === 0) return;
            
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) { // Shift + Tab
                if (document.activeElement === firstElement) {
                    lastElement.focus();
                    e.preventDefault();
                }
            } else { // Tab
                if (document.activeElement === lastElement) {
                    firstElement.focus();
                    e.preventDefault();
                }
            }
        }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && study && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        >
          <motion.div 
            id="casestudy-modal-box"
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-[#202222] border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-3xl overflow-hidden shadow-2xl relative text-zinc-800 dark:text-zinc-200 max-h-[90vh] flex flex-col"
          >
        {study.image && (
          <div className="relative h-48 sm:h-56 bg-slate-900 overflow-hidden flex items-center justify-center border-b border-slate-100 dark:border-slate-800/80">
            {study.id === "jenga-realty" ? (
              <div className="flex space-x-6 items-center py-6">
                <img src={study.image} className="h-32 object-contain" alt={study.title} />
                {study.imageAlt && <img src={study.imageAlt} className="h-32 object-contain hidden sm:block" alt={study.title} />}
              </div>
            ) : (
              <img src={study.image} className="w-full h-full object-cover opacity-60" alt={study.title} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-[#202222] via-transparent to-transparent"></div>
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2.5 rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur-md transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              aria-label="Close modal"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Modal content body */}
        <div className="p-6 lg:p-8 overflow-y-auto flex-grow space-y-6">
          <div className="space-y-2">
            <span className="inline-block px-3 py-1 rounded-full text-[10px] font-semibold bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/30 uppercase tracking-wider">
              {study.category}
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white leading-tight">
              {study.title}
            </h3>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm font-medium leading-relaxed">
              {study.subtitle}
            </p>
          </div>

          <div className="border-t border-slate-200 dark:border-slate-800/60 my-4"></div>

          {/* Problem & Solution */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">The Challenge</h4>
              <p className="text-sm font-light text-zinc-600 dark:text-zinc-300 leading-relaxed">
                {study.problem}
              </p>
            </div>
            <div className="space-y-2.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Jibu Labs AI Solution</h4>
              <p className="text-sm font-light text-zinc-600 dark:text-zinc-300 leading-relaxed">
                {study.solution}
              </p>
            </div>
          </div>

          {/* Metrics Section */}
          <div className="bg-slate-50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-800/80 rounded-2xl p-5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-4">Key Business Outcomes</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              {study.metrics && study.metrics.map((metric, index) => (
                <div key={index} className="space-y-1">
                  <div className="text-2xl sm:text-3xl font-extrabold text-purple-600 dark:text-purple-400">
                    {metric.value}
                  </div>
                  <div className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 font-light leading-tight">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Technology Stack Tags */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Integration Stack</h4>
            <div className="flex flex-wrap gap-2">
              {study.tech && study.tech.map((t, idx) => (
                <span 
                  key={idx} 
                  className="px-2.5 py-1 rounded-lg text-xs font-mono bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700/50 shadow-sm"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 dark:border-slate-800/60 pt-4">
            <p className="text-xs text-zinc-500 dark:text-zinc-400 font-light leading-relaxed">
              <strong>Impact Overview:</strong> {study.impact}
            </p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-slate-50 dark:bg-[#1c1d1d] border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-5 py-2 rounded-full border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/50 text-xs font-semibold text-zinc-700 dark:text-zinc-300 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            Close Viewer
          </button>
          <a
            href="/contact"
            onClick={onClose}
            className="px-5 py-2 rounded-full bg-purple-650 dark:bg-purple-600 text-white hover:bg-purple-750 dark:hover:bg-purple-500 text-xs font-semibold shadow-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-purple-650"
          >
            Inquire About Similiar Custom Solutions
          </a>
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CaseStudyModal;
